import styles from './Safety.module.css';

export default function Safety() {
  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Why MyPeopleDatabase is Safe to Use</h1>
        <p className={styles.intro}>
          Your data security and privacy are our top priorities. Here's why you can trust
          MyPeopleDatabase with your most valuable information—your relationships.
        </p>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔒 Your Data Stays on Your Device</h2>
          <p>
            Unlike cloud-based contact managers, MyPeopleDatabase stores all your data locally on
            your computer. Your contacts, notes, and relationships never leave your device unless
            you explicitly choose to share them. This means:
          </p>
          <ul>
            <li>No company has access to your personal data</li>
            <li>No risk of server breaches or data leaks</li>
            <li>Complete control over who sees your information</li>
            <li>Works offline—no internet connection required</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🛡️ Industry-Standard Security</h2>
          <p>
            When you do choose to use optional cloud features (like MPD ID for sharing), we use
            enterprise-grade security:
          </p>
          <ul>
            <li>
              <strong>End-to-end encryption</strong> for all data in transit
            </li>
            <li>
              <strong>Firebase Authentication</strong> with secure password hashing
            </li>
            <li>
              <strong>HTTPS/TLS</strong> for all web communications
            </li>
            <li>
              <strong>Regular security audits</strong> and updates
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔐 No Tracking, No Ads, No Selling</h2>
          <p>We have a simple business model: you pay once, we deliver software. This means:</p>
          <ul>
            <li>We don't track your behavior</li>
            <li>We don't show you ads</li>
            <li>We don't sell your data to third parties</li>
            <li>We don't build profiles on you</li>
            <li>Your relationships are yours, not our product</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔓 Open and Transparent</h2>
          <p>We believe in transparency about how your data is handled:</p>
          <ul>
            <li>
              Clear <a href="/privacy-policy">Privacy Policy</a> written in plain language
            </li>
            <li>
              Detailed <a href="/data-principles">Data Principles</a> explaining our philosophy
            </li>
            <li>SQLite database format you can inspect and export anytime</li>
            <li>No vendor lock-in—your data is portable</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>💾 You Own Your Data</h2>
          <p>Complete data ownership means:</p>
          <ul>
            <li>Export your entire database at any time</li>
            <li>Standard SQLite format readable by many tools</li>
            <li>No subscription required to access your own data</li>
            <li>Delete everything permanently whenever you want</li>
            <li>No hidden copies or backups we control</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔍 Optional Features, Your Choice</h2>
          <p>
            Advanced features like AI insights are completely optional and run locally on your
            device:
          </p>
          <ul>
            <li>AI processing happens on your computer, not our servers</li>
            <li>You choose which features to enable</li>
            <li>Core functionality works without any cloud services</li>
            <li>Sharing features require explicit opt-in</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🏢 Built by a Real Company</h2>
          <p>MyPeopleDatabase is developed by Object Nirvana, Inc.:</p>
          <ul>
            <li>Registered business with real accountability</li>
            <li>Responsive support team at support@mypeopledatabase.com</li>
            <li>Long-term commitment to the product</li>
            <li>Professional development practices and code quality</li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔄 Regular Updates</h2>
          <p>We continuously improve security and reliability:</p>
          <ul>
            <li>Regular security patches and updates</li>
            <li>Bug fixes and stability improvements</li>
            <li>
              Transparent <a href="/release-notes">release notes</a> for every update
            </li>
            <li>Active development and maintenance</li>
          </ul>
        </div>

        <div className={styles.callout}>
          <h3>Questions About Security?</h3>
          <p>
            We're happy to answer any security or privacy questions. Contact us at{' '}
            <a href="mailto:support@mypeopledatabase.com">support@mypeopledatabase.com</a>
          </p>
        </div>

        <div className={styles.cta}>
          <h3>Ready to Take Control of Your Data?</h3>
          <p>
            Join thousands of users who trust MyPeopleDatabase with their most important
            relationships.
          </p>
          <a href="/download" className={styles.ctaButton}>
            Download Now
          </a>
        </div>
      </div>
    </div>
  );
}
